/**
 * Conversation — AI Elements pattern
 * Scrollable message container with auto-scroll-to-bottom behavior.
 */
import React, { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface ConversationProps {
  children: ReactNode;
  className?: string;
}

export const Conversation: React.FC<ConversationProps> = ({ children, className }) => {
  return (
    <div className={cn('flex flex-col min-h-0 flex-1', className)}>
      {children}
    </div>
  );
};

interface ConversationContentProps {
  children: ReactNode;
  className?: string;
  onScrollableChange?: (isScrollable: boolean) => void;
}

export const ConversationContent: React.FC<ConversationContentProps> = ({
  children,
  className,
  onScrollableChange,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastScrollableRef = useRef<boolean | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });

    if (!onScrollableChange) return;
    const isScrollable = el.scrollHeight > el.clientHeight + 1;
    if (lastScrollableRef.current !== isScrollable) {
      lastScrollableRef.current = isScrollable;
      onScrollableChange(isScrollable);
    }
  });

  return (
    <div ref={scrollRef} className={cn('flex-1 min-h-0 overflow-y-auto', className)}>
      <div className="flex flex-col gap-2 p-2">
        {children}
      </div>
    </div>
  );
};

interface ConversationEmptyStateProps {
  title?: string;
  description?: string;
  icon?: ReactNode;
  children?: ReactNode;
  className?: string;
}

export const ConversationEmptyState: React.FC<ConversationEmptyStateProps> = ({
  title = 'No messages yet',
  description,
  icon,
  children,
  className,
}) => {
  return (
    <div className={cn('flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center', className)}>
      {icon && <div className="text-muted-foreground">{icon}</div>}
      <div className="text-sm assistant-title">{title}</div>
      {description && <div className="text-xs assistant-empty-desc max-w-[200px]">{description}</div>}
      {children}
    </div>
  );
};
